<script lang="ts">
  import { createQuery } from '@tanstack/svelte-query';
  import { dashboardApi, type Dashboard } from '$lib/api/dashboard';
  import { getErrorMessage } from '$lib/api/client';
  import { session } from '$lib/state/session.svelte';
  import { route } from '$lib/state/route.svelte';
  import { roleProfile, workflowAccessKey } from '$lib/utils/roleProfile';
  import { formatCompactMoney } from '$lib/utils/money';

  // Administrator oversight: one row per accessible pipeline, each from the
  // same server aggregate the dashboard uses. Team figures, not personal work.
  const profile = $derived(roleProfile(session.me));
  const teams = $derived((session.me?.verticals ?? []).filter(team => session.selectedVertical === 'all' || team.id === session.selectedVertical));
  const pulse = createQuery(() => ({
    queryKey: ['team-pulse', profile, session.me?.user.id, session.selectedVertical, workflowAccessKey(session.me)], enabled: profile === 'oversight' && teams.length > 0,
    queryFn: () => Promise.all(teams.map(async team => ({ team, data: await dashboardApi.get(team.id) }))), staleTime: 60000
  }));
  const shades = ['#10223a', '#24446b', '#226a73', '#5f9ca3', '#97d700', '#7ab000', '#b9bdbe', '#dbdbdb'];
  function value(data: Dashboard) {
    const buckets = data.pipeline.by_currency?.length ? data.pipeline.by_currency : [{ currency: data.pipeline.currency || 'INR', total_minor: data.pipeline.total_minor }];
    return buckets.map(bucket => formatCompactMoney(bucket.total_minor / 100, bucket.currency)).join(' · ');
  }
  function stages(data: Dashboard) {
    const total = data.pipeline.by_stage.reduce((sum, stage) => sum + stage.count, 0);
    return data.pipeline.by_stage.filter(stage => stage.count > 0).map((stage, index) => ({ ...stage, color: shades[index % shades.length], pct: total ? stage.count / total * 100 : 0 }));
  }
</script>

{#if profile === 'oversight'}
  <section class="day-card" aria-labelledby="team-pulse">
    <div class="flex flex-wrap items-baseline justify-between gap-2">
      <h2 id="team-pulse" class="day-title">Teams at a glance</h2>
      <span class="day-meta">Server aggregates per pipeline · hover a bar for its stage</span>
    </div>
    {#if pulse.isPending}<p role="status" class="day-meta mt-3">Loading team figures…</p>
    {:else if pulse.isError}<p role="alert" class="mt-3 text-sm text-red-700">{getErrorMessage(pulse.error)}</p><button class="day-mini-btn mt-2" onclick={() => pulse.refetch()}>Retry</button>
    {:else}
      <div class="mt-3 overflow-x-auto">
        <table class="w-full min-w-[820px] border-collapse text-sm">
          <thead><tr class="text-left text-xs text-dark-gray"><th class="px-1.5 py-2 font-medium">Team</th><th class="w-[34%] px-1.5 py-2 font-medium">Open deals by stage</th><th class="px-1.5 py-2 text-right font-medium">Open value</th><th class="px-1.5 py-2 text-right font-medium">New enquiries · 7d</th><th class="px-1.5 py-2 text-right font-medium">Open cases</th><th class="px-1.5 py-2 font-medium">Oldest open case</th></tr></thead>
          <tbody>
            {#each pulse.data ?? [] as row (row.team.id)}
              {@const bars = stages(row.data)}
              {@const cases = row.data.issues.open + row.data.issues.in_progress}
              <tr class="border-t border-[#eeedea] hover:bg-[#fafaf8]">
                <td class="px-1.5 py-3"><button class="font-semibold text-navy hover:underline" onclick={() => route.push({ tab: 'deals', vertical: String(row.team.id) })}>{row.team.name}</button><span class="block text-xs text-dark-gray">{row.data.pipeline.deal_count} open {row.data.pipeline.deal_count === 1 ? 'deal' : 'deals'}</span></td>
                <td class="px-1.5 py-3">
                  {#if bars.length}<div class="flex h-3.5 gap-0.5 overflow-hidden rounded-full" role="img" aria-label={bars.map(bar => `${bar.stage} ${bar.count}`).join(', ')}>{#each bars as bar (bar.stage)}<span style="width: {bar.pct}%; background: {bar.color}" title="{bar.stage}: {bar.count}"></span>{/each}</div>
                  {:else}<span class="text-xs text-dark-gray">No open deals</span>{/if}
                </td>
                <td class="px-1.5 py-3 text-right font-semibold">{value(row.data)}</td>
                <td class="px-1.5 py-3 text-right">{row.data.leads.new_this_week}</td>
                <td class="px-1.5 py-3 text-right">{#if cases}<span class="day-chip {cases >= 3 ? 'day-chip-danger' : 'day-chip-warn'}">{cases}</span>{:else}0{/if}</td>
                <td class="px-1.5 py-3 text-xs">{#if row.data.issues.oldest_open}<button class="text-left text-navy hover:underline" onclick={() => route.push({ tab: 'issues', id: row.data.issues.oldest_open!.id })}>#{row.data.issues.oldest_open.id} · {row.data.issues.oldest_open.title}</button>{:else}<span class="text-dark-gray">—</span>{/if}</td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
      <p class="mt-2 text-xs text-dark-gray">Open means not yet won or lost. Won deals may still need installation and a happy call.</p>
    {/if}
  </section>
{/if}
